/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { theme } from "@nordcloud/gnui";
import { KeyValue, DiscoveryRuleMatch } from "~/generated/graphql";
import { isNotEmpty } from "~/tools";
import { RuleType, DiscoveryRuleMatchInput } from "../../types";
import { mapLabels } from "../../utils";
import { AddAdditionalRule } from "../AddAdditionalRule";
import { AccountListItemValue } from "./AccountListItemValue";
import { ListItem } from "./ListItem";
import { ListItemValue, ListItemValueProps } from "./ListItemValue";

type Props = {
  match: DiscoveryRuleMatchInput;
  onDelete: (ruleType: string, rule?: KeyValue | string) => void;
  onAdd: (item: DiscoveryRuleMatch) => void;
  editable: boolean;
  isIbmResourceGroup?: boolean;
};

type RuleValue = ListItemValueProps["value"];

export function DiscoveryRuleMatchList({
  match,
  onDelete,
  onAdd,
  editable,
  isIbmResourceGroup = false,
}: Props) {
  return (
    <ul>
      {Object.entries(match).map(([key, value = []]) =>
        value != null && isNotEmpty(value as RuleValue[]) ? (
          <ListItem
            key={key}
            label={filterLabels(key as RuleType, isIbmResourceGroup)}
            deletable={editable}
            onClick={() => onDelete(key)}
          >
            <ul
              css={{
                padding: `${theme.spacing.spacing02} ${theme.spacing.spacing02}`,
              }}
            >
              {(value as RuleValue[]).map((rule, index) =>
                isAccount(key) ? (
                  <AccountListItemValue
                    key={JSON.stringify(rule)}
                    label={`#${index + 1}`}
                    value={rule as string}
                    onClick={() => onDelete(key, rule)}
                  />
                ) : (
                  <ListItemValue
                    key={JSON.stringify(rule)}
                    label={`#${index + 1}`}
                    value={rule}
                    onClick={() => onDelete(key, rule)}
                  />
                )
              )}
              {editable && value && value.length > 0 && (
                <AddAdditionalRule
                  selector={mapSelector(key as RuleType, isIbmResourceGroup)}
                  label={filterLabels(key as RuleType, isIbmResourceGroup)}
                  excludeSelectorItems={
                    Object.values(match)
                      .flat()
                      .filter((item) => typeof item === "string") as string[]
                  }
                  onDelete={onDelete}
                  onSubmit={(item) => onAdd({ [key]: [item] })}
                />
              )}
            </ul>
          </ListItem>
        ) : null
      )}
    </ul>
  );
}

// check if rule value should be mapped with providerId
function isAccount(key: string) {
  return (
    key === RuleType.CLOUD_ACCOUNT ||
    key === RuleType.PROJECT_ID ||
    key === RuleType.RESOURCE_GROUP
  );
}

function filterLabels(selector: RuleType, isIbmResourceGroup: boolean) {
  const key =
    isIbmResourceGroup && selector.includes(RuleType.RESOURCE_GROUP)
      ? RuleType.IBM_RESOURCE_GROUP
      : selector;
  return mapLabels(key);
}

function mapSelector(selector: RuleType, isIbmResourceGroup: boolean) {
  if (selector.includes(RuleType.RESOURCE_GROUP) && isIbmResourceGroup) {
    return RuleType.IBM_RESOURCE_GROUP;
  }
  return selector;
}
